import React, { useState, useEffect, useMemo } from 'react';
import { tkr, fmt, fmtDate, getISOWeek } from '../utils';
import { getDailyUnits, getDailyHours } from '../qlik';

// ── EXPORT-KNAPPAR (kopiera/spara kort som märkt PNG) ──────────────
// Fångar närmaste .card-förälder (eller target-ref). Bilden märks med logga.
export function ExportBtn({ name = 'graf', target }) {
  const [busy, setBusy] = useState(''); // '', 'copy', 'copied', 'save'
  const getNode = (e) => (target && target.current) || e.currentTarget.closest('.card');
  const doCopy = async (e) => {
    const node = getNode(e); if (!node) return;
    setBusy('copy');
    try {
      const { copyNodePng } = await import('../utils/exportImage');
      await copyNodePng(node);
      setBusy('copied');
      setTimeout(() => setBusy(''), 1600);
    } catch (err) { setBusy(''); alert('Kunde inte kopiera: ' + (err?.message || err)); }
  };
  const doSave = async (e) => {
    const node = getNode(e); if (!node) return;
    setBusy('save');
    try {
      const { exportNodePng } = await import('../utils/exportImage');
      await exportNodePng(node, name);
    } catch (err) { alert('Kunde inte spara bild: ' + (err?.message || err)); }
    finally { setBusy(''); }
  };
  const base = { border: '1px solid var(--border)', borderRadius: 8, cursor: busy ? 'default' : 'pointer',
    fontSize: 12, lineHeight: 1, padding: '5px 10px', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap' };
  return (
    <span data-export-skip="1" data-noprint="1" style={{ display: 'inline-flex', gap: 6 }}>
      <button onClick={doCopy} disabled={!!busy} title="Kopiera bild till urklipp" aria-label="Kopiera bild"
        style={{ ...base, background: busy === 'copied' ? '#E8F3EC' : '#1d6a5f', color: busy === 'copied' ? '#1A6E3C' : '#fff', borderColor: busy === 'copied' ? '#9FCBAF' : '#1d6a5f' }}>
        {busy === 'copied' ? '✓ Kopierad' : busy === 'copy' ? '…' : '⧉ Kopiera'}
      </button>
      <button onClick={doSave} disabled={!!busy} title="Spara som bild (PNG)" aria-label="Spara som PNG"
        style={{ ...base, background: 'none', color: 'var(--text-secondary)' }}>
        {busy === 'save' ? '…' : '⤓ PNG'}
      </button>
    </span>
  );
}

// ── CLOCK ─────────────────────────────────────────────────────────
export function Clock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 10000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="clock-wrap">
      <span className="clock-time">
        {time.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
      </span>
      <span className="clock-date">
        {time.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </span>
    </div>
  );
}

// ── SUBNAV ────────────────────────────────────────────────────────
export function SubNav({ tabs, active, onChange }) {
  return (
    <div className="subnav">
      {tabs.map(t => (
        <button
          key={t.id}
          className={'subnav-tab ' + (active === t.id ? 'active' : '')}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── METRIC ────────────────────────────────────────────────────────
export function Metric({ label, value, sub, trend }) {
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {sub && <div className={'metric-sub ' + (trend || '')}>{sub}</div>}
    </div>
  );
}

// ── KPI ROW ───────────────────────────────────────────────────────
export function KpiRow({ label, value, badge, sub, valueSub }) {
  return (
    <div className="kpi-row">
      <div>
        <span className="kpi-name">{label}</span>
        {sub && <div className="kpi-sub-text">{sub}</div>}
      </div>
      <div style={{ textAlign: 'right' }}>
        <span className="kpi-val">
          {value}
          {badge && <span className={'badge badge-' + badge.type}>{badge.text}</span>}
        </span>
        {valueSub && <div style={{ textAlign: 'right' }}>{valueSub}</div>}
      </div>
    </div>
  );
}

// ── BAR ROW (horisontell) ─────────────────────────────────────────
export function BarRow({ label, value, max, color, formatted }) {
  const p = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="bar-row">
      <div className="bar-label">{label}</div>
      <div className="bar-track">
        <div className="bar-fill" style={{ width: p + '%', background: color }} />
      </div>
      <div className="bar-val">{formatted}</div>
    </div>
  );
}

// ── COLUMN CHART (stående staplar) ────────────────────────────────
export function ColumnChart({ data, color = '#1B2A4A', formatVal }) {
  // data: [{ label, value }]
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => Math.abs(d.value)), 1);
  return (
    <div className="column-chart">
      {data.map((d, i) => {
        const heightPct = Math.round((Math.abs(d.value) / max) * 100);
        const isNeg = d.value < 0;
        return (
          <div key={i} className="column-wrap">
            <div className="column-val">{formatVal ? formatVal(d.value) : d.value}</div>
            <div className="column-bar-container">
              <div
                className="column-bar"
                style={{
                  height: heightPct + '%',
                  background: isNeg ? '#D85A30' : color,
                }}
              />
            </div>
            <div className="column-label">{d.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── TREND-JÄMFÖRELSE (i år vs föregående år, dubbel area + hover) ─
export function TrendCompare({ data, formatVal, curColor = '#2E6E8E', prevColor = '#A33D5B', curLabel = 'I år', prevLabel = 'Fg år', utanDiff = false }) {
  const [hov, setHov] = useState(null);
  if (!data || data.length === 0) return null;
  const W = 1000, H = 300, padL = 10, padR = 10, padT = 26, padB = 28;
  const n = data.length;
  // null-värden = glapp (t.ex. Personal% för ~stängda veckor) → utesluts ur skala + linje
  const isNum = (v) => v != null && isFinite(v);
  const nums = data.flatMap(d => [d.value, d.prev]).filter(isNum);
  const maxV = Math.max(...nums, 1);
  const minV = Math.min(...nums, 0); // negativa serier (t.ex. budgetresultat) får plats i bilden
  const span = maxV - minV || 1;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const x = (i) => (n === 1 ? padL + plotW / 2 : padL + (i * plotW) / (n - 1));
  const y = (v) => padT + (1 - ((v || 0) - minV) / span) * plotH;
  const y0 = y(0); // nollinjen — areornas basnivå
  const pts = (key) => data.map((d, i) => ({ i, v: d[key] })).filter(p => isNum(p.v));
  const line = (key) => pts(key).map((p, j) => `${j === 0 ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const area = (key) => { const p = pts(key); if (p.length < 2) return ''; return `${line(key)} L${x(p[p.length - 1].i).toFixed(1)},${y0.toFixed(1)} L${x(p[0].i).toFixed(1)},${y0.toFixed(1)} Z`; };
  const showLabels = n <= 14;
  const labelStep = Math.max(1, Math.ceil(n / 14));
  const fmt = (v) => (formatVal ? formatVal(v) : String(Math.round(v)));
  const hd = hov != null ? data[hov] : null;
  const hDiff = !utanDiff && hd && isNum(hd.value) && isNum(hd.prev) && hd.prev !== 0 ? (hd.value - hd.prev) / Math.abs(hd.prev) * 100 : null;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end', marginBottom: 2, fontFamily: 'system-ui,sans-serif', fontSize: 11, color: 'var(--text-secondary)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 3, borderRadius: 2, background: curColor }} />{curLabel}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 3, borderRadius: 2, background: prevColor }} />{prevLabel}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }} onMouseLeave={() => setHov(null)}>
        {minV < 0 && <line x1={padL} y1={y0} x2={W - padR} y2={y0} stroke="#999" strokeWidth="1" strokeDasharray="2 3" />}
        <path d={area('prev')}  fill={prevColor} opacity="0.16" />
        <path d={area('value')} fill={curColor}  opacity="0.18" />
        <path d={line('prev')}  fill="none" stroke={prevColor} strokeWidth="1.8" strokeDasharray="5 3" />
        <path d={line('value')} fill="none" stroke={curColor}  strokeWidth="2.5" />
        {data.map((d, i) => (
          <g key={i}>
            {isNum(d.prev)  && <circle cx={x(i)} cy={y(d.prev)}  r="2.5" fill={prevColor} />}
            {isNum(d.value) && <circle cx={x(i)} cy={y(d.value)} r="3"   fill={curColor} />}
            {showLabels && hov === null && isNum(d.value) && (
              <text x={x(i)} y={y(d.value) - 8} textAnchor="middle" fontSize="10.5" fontWeight="600" fill={curColor} fontFamily="system-ui,sans-serif">{fmt(d.value)}</text>
            )}
            {i % labelStep === 0 && (
              <text x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#999" fontFamily="system-ui,sans-serif">{d.label}</text>
            )}
          </g>
        ))}
        {/* osynliga hover-zoner */}
        {data.map((d, i) => {
          const xl = i === 0 ? 0 : (x(i - 1) + x(i)) / 2;
          const xr = i === n - 1 ? W : (x(i) + x(i + 1)) / 2;
          return <rect key={'h' + i} x={xl} y="0" width={Math.max(1, xr - xl)} height={H} fill="transparent" onMouseEnter={() => setHov(i)} />;
        })}
        {/* tooltip */}
        {hd && (() => {
          const bw = 168, bh = 60;
          const bx = Math.max(2, Math.min(W - bw - 2, x(hov) - bw / 2));
          const anchorY = isNum(hd.value) ? y(hd.value) : (isNum(hd.prev) ? y(hd.prev) : padT + plotH);
          const by = Math.max(2, anchorY - bh - 8);
          return (
            <g pointerEvents="none">
              <line x1={x(hov)} y1={padT} x2={x(hov)} y2={padT + plotH} stroke="#bbb" strokeWidth="1" strokeDasharray="3 3" />
              {isNum(hd.prev)  && <circle cx={x(hov)} cy={y(hd.prev)}  r="4.5" fill={prevColor} />}
              {isNum(hd.value) && <circle cx={x(hov)} cy={y(hd.value)} r="5"   fill={curColor} />}
              <rect x={bx} y={by} width={bw} height={bh} rx="6" fill="#fff" stroke="#ddd" />
              <text x={bx + 11} y={by + 18} fontSize="11.5" fontWeight="700" fill="#333" fontFamily="system-ui,sans-serif">
                {hd.label}{hDiff !== null ? `   ${hDiff >= 0 ? '+' : ''}${hDiff.toFixed(0)}%` : ''}
              </text>
              <text x={bx + 11} y={by + 36} fontSize="11" fill={curColor}  fontFamily="system-ui,sans-serif">{curLabel}: {isNum(hd.value) ? fmt(hd.value) : '–'}</text>
              <text x={bx + 11} y={by + 52} fontSize="11" fill={prevColor} fontFamily="system-ui,sans-serif">{prevLabel}: {isNum(hd.prev) ? fmt(hd.prev) : '–'}</text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ── FÖR PLANERING (fjolårets dagar projicerade på kommande 4 veckor) ─
// Visar förra årets faktiska oms + personalkostnad per dag, matchat på
// SAMMA VECKODAG (−364 dgr = 52 v bakåt), som planeringsunderlag framåt.
// `enheter` = lista med Resultatenhet-namn att summera (t.ex. Brasserie =
// ['Brasserie Kerstin','Vävstugan']). `color` = blockets färg.
export function PlaneringTab({ enheter, color = '#1B2A4A', splitConfig = null }) {
  const [rows, setRows]   = useState(null);
  const [split, setSplit] = useState(null);
  const [hov, setHov]     = useState(null);
  const [view, setView]   = useState('total'); // 'total' | '0' (dag) | '1' (kväll)
  // Sökbar period — default = idag + 4 veckor (oförändrat läge)
  const [startSel, setStartSel] = useState('');
  const [veckor, setVeckor]     = useState(4);

  useEffect(() => {
    getDailyUnits().then(setRows).catch(() => setRows([]));
    if (splitConfig?.loader) splitConfig.loader().then(setSplit).catch(() => setSplit({}));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (rows === null) {
    return <div className="card"><p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar planeringsdata…</p></div>;
  }

  const enhSet = new Set(enheter);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const WD = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
  const splitOn = !!splitConfig && view !== 'total';
  const sIdx = view === '1' ? 1 : 0;

  // Slå ihop fjolårets dagsvärden per datum.
  // view='total' → daily_units (alla valda enheter). view='0'/'1' → dag/kväll-split.
  const byDate = {};
  if (splitOn) {
    for (const [datum, v] of Object.entries(split || {})) {
      byDate[datum] = { oms: v.oms[sIdx] || 0, pers: v.pers[sIdx] || 0 };
    }
  } else {
    for (const r of rows) {
      if (!enhSet.has(r.enhet)) continue;
      if (!byDate[r.datum]) byDate[r.datum] = { oms: 0, pers: 0 };
      byDate[r.datum].oms  += r.intakt || 0;
      byDate[r.datum].pers += Math.abs(r.lon || 0);
    }
  }

  // Bygg N×7 dagar från vald startdag, hämta fjolårets matchande veckodag (−364 dgr)
  const today = new Date(); today.setHours(12, 0, 0, 0);
  const start = startSel ? new Date(startSel + 'T12:00:00') : today;
  const nDagar = veckor * 7;
  const days = [];
  for (let i = 0; i < nDagar; i++) {
    const d = new Date(start); d.setDate(d.getDate() + i);
    const src = new Date(d);   src.setDate(src.getDate() - 364);
    const v = byDate[iso(src)] || { oms: 0, pers: 0 };
    days.push({
      i, d, src, week: Math.floor(i / 7),
      wd: d.getDay(), wdLbl: WD[d.getDay()], dnum: d.getDate(), mon: d.getMonth() + 1,
      oms: v.oms, pers: v.pers,
    });
  }
  const maxV = Math.max(...days.map(d => d.oms), 1);

  // Veckosummering
  const weeks = Array.from({ length: veckor }, (_, w) => {
    const ds = days.filter(x => x.week === w);
    const oms  = ds.reduce((s, x) => s + x.oms, 0);
    const pers = ds.reduce((s, x) => s + x.pers, 0);
    return { w, oms, pers, pct: oms > 0 ? Math.round(pers / oms * 100) : null,
             vecka: getISOWeek(ds[0].d), from: ds[0].d, to: ds[ds.length - 1].d };
  });

  // SVG-geometri
  const W = 1000, H = 300, padL = 8, padR = 8, padT = 30, padB = 48;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const slotW = plotW / nDagar;
  const barW  = slotW * 0.64;
  const xC    = (i) => padL + (i + 0.5) * slotW;
  const yTop  = (val) => padT + (1 - Math.min(val, maxV) / maxV) * plotH;
  const baseY = padT + plotH;
  const hd = hov != null ? days[hov] : null;

  return (
    <div style={{ fontFamily: 'system-ui,sans-serif' }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>
            För planering · {startSel ? `${veckor} veckor från ${startSel}` : `kommande ${veckor} veckor`}
          </div>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-secondary)' }}>
            <span>Från</span>
            <input type="date" value={startSel} onChange={(e) => setStartSel(e.target.value)} aria-label="Startdatum"
              style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '3px 8px', fontSize: 11.5, fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)' }} />
            {[2, 4, 8].map(v => (
              <button key={v} onClick={() => setVeckor(v)} style={{
                fontSize: 11.5, fontWeight: 600, padding: '4px 11px', borderRadius: 999,
                border: '1px solid ' + (veckor === v ? color : 'var(--border)'),
                background: veckor === v ? color : 'transparent',
                color: veckor === v ? '#fff' : 'var(--text-secondary)', cursor: 'pointer',
              }}>{v} v</button>
            ))}
            {(startSel || veckor !== 4) && (
              <button onClick={() => { setStartSel(''); setVeckor(4); }} style={{
                fontSize: 11.5, fontWeight: 600, padding: '4px 11px', borderRadius: 999,
                border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
              }}>Nollställ</button>
            )}
          </div>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '6px 0' }}>
          Förra årets faktiska siffror för <strong>samma veckodag</strong> (52 veckor bakåt) — som underlag när du planerar framåt.
        </p>
        {splitConfig && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {[['total', 'Totalt'], ['0', splitConfig.labels[0]], ['1', splitConfig.labels[1]]].map(([id, lbl]) => (
              <button key={id} onClick={() => setView(id)}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '5px 13px', borderRadius: 999,
                  border: '1px solid ' + (view === id ? color : 'var(--border)'),
                  background: view === id ? color : 'transparent',
                  color: view === id ? '#fff' : 'var(--text-secondary)', cursor: 'pointer',
                }}>{lbl}</button>
            ))}
          </div>
        )}
        {splitOn && split === null && <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Laddar {splitConfig.labels[sIdx].toLowerCase()}sdata…</p>}
        <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end', marginBottom: 2, fontSize: 11, color: 'var(--text-secondary)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 9, borderRadius: 2, background: color, opacity: 0.28 }} />Omsättning</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 9, borderRadius: 2, background: color }} />Personalkostnad</span>
        </div>

        <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }} onMouseLeave={() => setHov(null)}>
          {/* veckoskiljare + veckonummer */}
          {Array.from({ length: veckor - 1 }, (_, k) => k + 1).map(w => {
            const sx = padL + w * 7 * slotW;
            return <line key={'sep' + w} x1={sx} y1={padT - 6} x2={sx} y2={baseY} stroke="#e2e2e2" strokeWidth="1" />;
          })}
          {weeks.map(wk => {
            const cx = padL + (wk.w * 7 + 3.5) * slotW;
            return <text key={'wl' + wk.w} x={cx} y={padT - 14} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#aaa">v.{wk.vecka}</text>;
          })}
          {/* staplar */}
          {days.map((d, i) => {
            const weekend = d.wd === 0 || d.wd === 6;
            const omsTop  = yTop(d.oms);
            const persH   = Math.min(d.pers, d.oms) / maxV * plotH; // dark bas = personal
            const bx = xC(i) - barW / 2;
            return (
              <g key={i}>
                {weekend && <rect x={xC(i) - slotW / 2} y={padT - 6} width={slotW} height={plotH + 6} fill="var(--weekend-band, #f6f4ef)" />}
                {/* hel oms-stapel (ljus) */}
                <rect x={bx} y={omsTop} width={barW} height={Math.max(0, baseY - omsTop)} rx="2" fill={color} opacity={hov === i ? 0.42 : 0.26} />
                {/* personalkostnad (mörk bas) */}
                {persH > 0 && <rect x={bx} y={baseY - persH} width={barW} height={persH} rx="2" fill={color} opacity={hov === i ? 1 : 0.85} />}
                {/* veckodag + datum (i år) + fjolårets matchade datum */}
                <text x={xC(i)} y={baseY + 14} textAnchor="middle" fontSize="9" fill={weekend ? '#b08' : '#999'} fontWeight={weekend ? 700 : 400}>{d.wdLbl}</text>
                <text x={xC(i)} y={baseY + 26} textAnchor="middle" fontSize="9" fill="#bbb">{d.dnum}/{d.mon}</text>
                <text x={xC(i)} y={baseY + 37} textAnchor="middle" fontSize="7.5" fill="#cfc8bd">{d.src.getDate()}/{d.src.getMonth() + 1}</text>
                {/* hover-zon */}
                <rect x={xC(i) - slotW / 2} y={padT - 6} width={slotW} height={plotH + 40} fill="transparent" onMouseEnter={() => setHov(i)} />
              </g>
            );
          })}
          {/* tooltip */}
          {hd && (() => {
            const bw = 188, bh = 78;
            const bx = Math.max(2, Math.min(W - bw - 2, xC(hov) - bw / 2));
            const by = Math.max(2, yTop(hd.oms) - bh - 8);
            const pctv = hd.oms > 0 ? Math.round(hd.pers / hd.oms * 100) : null;
            return (
              <g pointerEvents="none">
                <rect x={bx} y={by} width={bw} height={bh} rx="6" fill="#fff" stroke="#ddd" />
                <text x={bx + 11} y={by + 17} fontSize="11.5" fontWeight="700" fill="#333">{hd.wdLbl} {hd.dnum}/{hd.mon}</text>
                <text x={bx + 11} y={by + 32} fontSize="10" fill="#999">i fjol: {fmtDate(iso(hd.src))}</text>
                <text x={bx + 11} y={by + 49} fontSize="11" fill={color}>Omsättning: {fmt(hd.oms)} kr</text>
                <text x={bx + 11} y={by + 64} fontSize="11" fill="#666">Personal: {fmt(hd.pers)} kr{pctv != null ? `  ·  ${pctv}%` : ''}</text>
              </g>
            );
          })()}
        </svg>

        <div className="note" style={{ marginTop: 8 }}>Källa: Qlik · fjolårets dagsdata · matchat på veckodag (−364 dgr) · översta datumet = i år, lilla grå = fjolårets dag{splitOn ? ' · dag/kväll avser Brasserie Kerstin (ej Vävstugan)' : ''}</div>
      </div>

      {/* Veckosummering */}
      <div className="card">
        <div className="card-title">Veckosummor (i fjol)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {weeks.map(wk => (
            <div key={wk.w} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Vecka {wk.vecka}</div>
              <div style={{ fontSize: 10.5, color: '#aaa', marginBottom: 8 }}>{wk.from.getDate()}/{wk.from.getMonth() + 1}–{wk.to.getDate()}/{wk.to.getMonth() + 1}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color }}>{tkr(wk.oms)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 4 }}>Personal: {tkr(wk.pers)}</div>
              <div style={{ fontSize: 11.5, color: wk.pct != null && wk.pct > 50 ? '#A32D2D' : 'var(--text-secondary)', marginTop: 2 }}>Personal%: {wk.pct != null ? wk.pct + '%' : '–'}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── NORDERI KPI ───────────────────────────────────────────────────
export function NorderiKpi({ emoji, label, value, sub, color }) {
  return (
    <div style={{ fontFamily: 'system-ui,sans-serif' }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>{emoji}</div>
      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── ENHET → DAGLIGT INTÄKTSVÄRDE ──────────────────────────────────
function dagligVal(d, enhet) {
  if (enhet === 'hotell')      return d.hotell      || 0;
  if (enhet === 'brasserie')   return d.brasserie   || 0;
  if (enhet === 'knallen')     return d.knallen     || 0;
  if (enhet === 'sportcenter') return d.sportcenter || 0;
  if (enhet === 'stugservice') return d.stugservice || 0;
  return d.totalt || 0;
}

// ── FJOL-RAD (YoY-jämförelse för coaching: "Ifjol X kr · +Y%") ──────
// cur=0 (t.ex. dagen har knappt börjat) → visa bara ifjol-beloppet, ingen %.
export function FjolRad({ cur, fjol, suffix = 'kr', fmt }) {
  if (!fjol || fjol <= 0) return null;
  const p = Math.round((cur - fjol) / fjol * 100);
  const up = cur >= fjol;
  return (
    <div style={{ marginTop: 2, fontSize: 11.5, fontFamily: 'system-ui,sans-serif', color: 'var(--text-secondary)' }}>
      Ifjol {fmt ? fmt(fjol) : `${Math.round(fjol).toLocaleString('sv-SE')} ${suffix}`}
      {cur > 0 && <span style={{ fontWeight: 700, color: up ? '#3B6D11' : '#A32D2D' }}> · {p >= 0 ? '+' : ''}{p}%</span>}
    </div>
  );
}

// ── VISA FLER-RAD (topplistor: 7 från start → hela listan) ─────────
export function VisaFlerRad({ total, expanded, onToggle, initial = 7 }) {
  if (total <= initial) return null;
  return (
    <div style={{ textAlign: 'center', marginTop: 8 }}>
      <button onClick={onToggle} style={{
        background: 'transparent', border: '1px solid var(--border)', borderRadius: 20,
        padding: '4px 14px', fontSize: 11, color: 'var(--text-secondary)',
        cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
      }}>
        {expanded ? 'Visa färre' : `Visa fler (${total - initial} till)`}
      </button>
    </div>
  );
}

// Flytta ett ISO-datum n dagar (−364 = samma veckodag ifjol)
export function shiftISO(str, days) {
  const d = new Date(str + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// Blockens enhet-nyckel → Resultatenhet-namn i daily_units (för fjolårsdata)
const UNITS_MAP = {
  brasserie:   ['Brasserie Kerstin', 'Vävstugan'],
  knallen:     ['Knallen'],
  sportcenter: ['Sportcenter'],
  stugservice: ['Stugservice'],
};

// ── DAGLIG SNABBRAD (Idag · Igår · Denna vecka · Hittills i månad) ──
export function DagligSnabb({ daily, enhet }) {
  // Fjolårssiffror ur daily_units-cachen (full historik, dag × Resultatenhet)
  const [units, setUnits] = useState(null);
  useEffect(() => { getDailyUnits().then(setUnits).catch(() => setUnits([])); }, []);
  if (!daily || daily.length === 0) return null;

  const today    = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const yest     = new Date(today); yest.setDate(today.getDate() - 1);
  const yestStr  = yest.toISOString().slice(0, 10);
  const dow      = today.getDay() === 0 ? 6 : today.getDay() - 1;
  const monday   = new Date(today); monday.setDate(today.getDate() - dow);
  const monStr   = monday.toISOString().slice(0, 10);
  const monthKey = todayStr.slice(0, 7); // "2026-06"

  const sum = (pred) => daily.reduce((s, d) => (pred(d) ? s + dagligVal(d, enhet) : s), 0);
  const idag  = sum(d => d.datum === todayStr);
  const igar  = sum(d => d.datum === yestStr);
  const vecka = sum(d => d.datum >= monStr && d.datum <= todayStr);
  const manad = sum(d => d.datum && d.datum.slice(0, 7) === monthKey);

  // Fjolår: dag/vecka matchas på samma veckodag (−364 dgr), månad på samma dag-i-månad
  const enhSet = new Set(UNITS_MAP[enhet] || []);
  const uSum = (from, to) => (units || []).reduce((s, u) => (u.datum >= from && u.datum <= to && enhSet.has(u.enhet)) ? s + (u.intakt || 0) : s, 0);
  const fjolIdag  = uSum(shiftISO(todayStr, -364), shiftISO(todayStr, -364));
  const fjolIgar  = uSum(shiftISO(yestStr, -364), shiftISO(yestStr, -364));
  const fjolVecka = uSum(shiftISO(monStr, -364), shiftISO(todayStr, -364));
  const lyMonthStart = `${today.getFullYear() - 1}-${todayStr.slice(5, 7)}-01`;
  const fjolManad = uSum(lyMonthStart, `${today.getFullYear() - 1}-${todayStr.slice(5)}`);

  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const manadNamn = cap(today.toLocaleDateString('sv-SE', { month: 'long' }));

  // Hela kronor i det stora beloppet — tkr är för grovt för dagsnivå
  const krHel = (v) => Math.round(v).toLocaleString('sv-SE') + ' kr';
  return (
    <div className="grid-4">
      <Metric label="Idag"            value={idag  !== 0 ? krHel(idag)  : '–'} sub={<>{fmtDate(todayStr)}<FjolRad cur={idag} fjol={fjolIdag} /></>} />
      <Metric label="Igår"            value={igar  !== 0 ? krHel(igar)  : '–'} sub={<>{fmtDate(yestStr)}<FjolRad cur={igar} fjol={fjolIgar} /></>} />
      <Metric label="Denna vecka"     value={vecka !== 0 ? krHel(vecka) : '–'} sub={<>{`V${getISOWeek(monday)}`}<FjolRad cur={vecka} fjol={fjolVecka} /></>} />
      <Metric label="Hittills i månad" value={manad !== 0 ? krHel(manad) : '–'} sub={<>{manadNamn}<FjolRad cur={manad} fjol={fjolManad} /></>} />
    </div>
  );
}

// ── DAGLIG FÖRSÄLJNING ────────────────────────────────────────────
export function DagligForsaljning({ daily, enhet }) {
  if (!daily || daily.length === 0) return null;

  function getVal(d) {
    return dagligVal(d, enhet);
  }

  const days = daily.slice(0, 14).map(d => ({ datum: d.datum, rev: getVal(d) }));

  function getWeekSum(weekOffset) {
    const today = new Date();
    const dow = today.getDay() === 0 ? 6 : today.getDay() - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - dow - weekOffset * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const monStr = monday.toISOString().slice(0, 10);
    const sunStr = sunday.toISOString().slice(0, 10);
    return daily.reduce((s, d) => d.datum >= monStr && d.datum <= sunStr ? s + getVal(d) : s, 0);
  }

  function veckaLabel(offset) {
    const today = new Date();
    const dow = today.getDay() === 0 ? 6 : today.getDay() - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - dow - offset * 7);
    const wn = getISOWeek(monday);
    return offset === 0 ? `V${wn} (pågående)` : `V${wn}`;
  }

  const maxDay = Math.max(...days.map(d => Math.abs(d.rev)), 1);

  return (
    <div className="card">
      <div className="card-title">Daglig försäljning — senaste 14 dagarna</div>
      <div className="grid-3" style={{ marginBottom: 12 }}>
        <Metric label={veckaLabel(0)} value={tkr(getWeekSum(0))} sub="Denna vecka" />
        <Metric label={veckaLabel(1)} value={tkr(getWeekSum(1))} sub="Förra veckan" />
        <Metric label={veckaLabel(2)} value={tkr(getWeekSum(2))} sub="2 veckor sedan" />
      </div>
      {days.map((d, i) => {
        const isPos = d.rev >= 0;
        const p = Math.round(Math.abs(d.rev) / maxDay * 100);
        return (
          <div key={i} className="bar-row">
            <div className="bar-label" style={{ minWidth: 80 }}>{fmtDate(d.datum)}</div>
            <div className="bar-track">
              <div className="bar-fill" style={{ width: p + '%', background: isPos ? '#1B2A4A' : '#D85A30' }} />
            </div>
            <div className="bar-val">{d.rev !== 0 ? tkr(d.rev) : '–'}</div>
          </div>
        );
      })}
      <div className="note">Källa: Qlik daglig data</div>
    </div>
  );
}

// ── YOY-UTFORSKAREN (linjediagram i år vs ifjol, för avdelningsblocken) ──
// Personalen kan gräva: mått (Omsättning/Personal%/OMS per h), period och
// upplösning. Data ur daily_units + daily_hours (full historik i cachen).
// Dag/vecka matchas mot samma veckodag ifjol (−364), månad mot samma kalendermånad.
const YOY_MANADER = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export function YoYUtforskare({ enheter, hoursEnhet, color = '#1B2A4A' }) {
  const [units, setUnits]   = useState(null);
  const [hours, setHours]   = useState(null);
  const [period, setPeriod] = useState('30d');  // '30d' | '90d' | '12m'
  const [gran, setGran]     = useState('dag');  // 'dag' | 'vecka' | 'manad'
  const [matt, setMatt]     = useState('oms');  // 'oms' | 'personal' | 'omsh'
  const [fromSel, setFromSel] = useState('');   // egen datumperiod (åsidosätter presets)
  const [toSel, setToSel]     = useState('');

  useEffect(() => {
    getDailyUnits().then(setUnits).catch(() => setUnits([]));
    getDailyHours().then(setHours).catch(() => setHours({ order: [], byDatum: {} }));
  }, []);

  // Dagliga summor för valda enheter
  const day = useMemo(() => {
    const enhSet = new Set(enheter);
    const oms = {}, lon = {};
    for (const u of (units || [])) {
      if (!enhSet.has(u.enhet)) continue;
      oms[u.datum] = (oms[u.datum] || 0) + (u.intakt || 0);
      lon[u.datum] = (lon[u.datum] || 0) + Math.abs(u.lon || 0);
    }
    return { oms, lon };
  }, [units, enheter]);

  const data = useMemo(() => {
    const hrsIdx = hours && hoursEnhet ? hours.order.indexOf(hoursEnhet) : -1;
    const hrsAt = (d) => hrsIdx >= 0 ? ((hours.byDatum[d] || [])[hrsIdx] || 0) : 0;
    const p2 = (n) => String(n).padStart(2, '0');
    const today = new Date();
    const todayISO = `${today.getFullYear()}-${p2(today.getMonth() + 1)}-${p2(today.getDate())}`;
    // Egen datumperiod (om båda fälten är satta) åsidosätter presets
    const custom = fromSel && toSel && fromSel <= toSel;
    const days = period === '30d' ? 29 : period === '90d' ? 89 : 364;
    const fromStr = custom ? fromSel : shiftISO(todayISO, -days);
    const endISO  = custom ? (toSel > todayISO ? todayISO : toSel) : todayISO;
    const spanDays = custom ? Math.round((new Date(endISO) - new Date(fromStr)) / 86400000) : days;
    // Dag-upplösning blir oläslig över ~90 dgr → tvinga vecka
    const granEff = (gran === 'dag' && spanDays > 92) ? 'vecka' : gran;

    const sumRange = (map, from, to) => { let s = 0; for (let d = from; d <= to; d = shiftISO(d, 1)) s += map[d] || 0; return s; };
    const sumHrs = (from, to) => { let s = 0; for (let d = from; d <= to; d = shiftISO(d, 1)) s += hrsAt(d); return s; };

    const buckets = [];
    if (granEff === 'dag') {
      for (let d = fromStr; d <= endISO; d = shiftISO(d, 1)) {
        buckets.push({ label: `${Number(d.slice(8, 10))}/${Number(d.slice(5, 7))}`, from: d, to: d });
      }
    } else if (granEff === 'vecka') {
      const fd = new Date(fromStr + 'T12:00:00Z');
      const dow = (fd.getUTCDay() + 6) % 7;
      let mon = shiftISO(fromStr, -dow);
      while (mon <= endISO) {
        buckets.push({ label: `v${getISOWeek(new Date(mon + 'T12:00:00Z'))}`, from: mon, to: shiftISO(mon, 6) });
        mon = shiftISO(mon, 7);
      }
    } else {
      let ym = Number(fromStr.slice(0, 4)) * 100 + Number(fromStr.slice(5, 7));
      const endYm = Number(endISO.slice(0, 4)) * 100 + Number(endISO.slice(5, 7));
      while (ym <= endYm) {
        const y = Math.floor(ym / 100), m = ym % 100;
        buckets.push({
          label: YOY_MANADER[m - 1],
          from: `${y}-${p2(m)}-01`, to: `${y}-${p2(m)}-${p2(new Date(y, m, 0).getDate())}`,
          pf: `${y - 1}-${p2(m)}-01`, pt: `${y - 1}-${p2(m)}-${p2(new Date(y - 1, m, 0).getDate())}`,
        });
        ym = m === 12 ? (y + 1) * 100 + 1 : ym + 1;
      }
    }

    const rows = buckets.map(b => {
      const pf = b.pf || shiftISO(b.from, -364);
      const pt = b.pt || shiftISO(b.to, -364);
      return {
        label: b.label,
        oms: sumRange(day.oms, b.from, b.to),  omsP: sumRange(day.oms, pf, pt),
        lon: sumRange(day.lon, b.from, b.to),  lonP: sumRange(day.lon, pf, pt),
        hrs: sumHrs(b.from, b.to),             hrsP: sumHrs(pf, pt),
      };
    });

    if (matt === 'personal') {
      // Uteslut ~stängda perioder (< 12% av toppen) → glapp i linjen, läsbar skala
      const floor  = Math.max(...rows.map(r => r.oms),  0) * 0.12;
      const floorP = Math.max(...rows.map(r => r.omsP), 0) * 0.12;
      return rows.map(r => ({
        label: r.label,
        value: r.oms  >= floor  && r.oms  > 0 ? Math.round(r.lon  / r.oms  * 100) : null,
        prev:  r.omsP >= floorP && r.omsP > 0 ? Math.round(r.lonP / r.omsP * 100) : null,
      }));
    }
    if (matt === 'omsh') {
      return rows.map(r => ({
        label: r.label,
        value: r.hrs  > 0 ? Math.round(r.oms  / r.hrs)  : null,
        prev:  r.hrsP > 0 ? Math.round(r.omsP / r.hrsP) : null,
      }));
    }
    return rows.map(r => ({ label: r.label, value: r.oms, prev: r.omsP }));
  }, [day, hours, hoursEnhet, period, gran, matt, fromSel, toSel]);

  const chip = (active) => ({
    background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
    fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
  });
  const fmtVal = matt === 'oms'
    ? (v) => tkr(v)
    : matt === 'personal' ? (v) => `${Math.round(v)}%` : (v) => `${Math.round(v).toLocaleString('sv-SE')} kr/h`;
  const customActive = !!(fromSel && toSel && fromSel <= toSel);
  const customSpan = customActive ? Math.round((new Date(toSel) - new Date(fromSel)) / 86400000) : 0;
  const granDisabled = customActive ? customSpan > 92 : period === '12m';
  const dateInp = {
    border: '1px solid var(--border)', borderRadius: 8, padding: '3px 8px',
    fontSize: 11.5, fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)',
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Utveckling — i år vs ifjol</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          <button onClick={() => setMatt('oms')}      style={chip(matt === 'oms')}>Omsättning</button>
          <button onClick={() => setMatt('personal')} style={chip(matt === 'personal')}>Personal%</button>
          {hoursEnhet && <button onClick={() => setMatt('omsh')} style={chip(matt === 'omsh')}>OMS/h</button>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, fontFamily: 'system-ui,sans-serif' }}>
        {[['30d', '30 dagar'], ['90d', '90 dagar'], ['12m', '12 månader']].map(([id, lab]) => (
          <button key={id} onClick={() => { setPeriod(id); setFromSel(''); setToSel(''); }}
            style={chip(period === id && !customActive)}>{lab}</button>
        ))}
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 3px' }} />
        {[['dag', 'Dag'], ['vecka', 'Vecka'], ['manad', 'Månad']].map(([id, lab]) => (
          <button key={id} onClick={() => setGran(id)}
            disabled={id === 'dag' && granDisabled}
            style={{ ...chip(gran === id && !(id === 'dag' && granDisabled)), opacity: id === 'dag' && granDisabled ? 0.4 : 1 }}>
            {lab}
          </button>
        ))}
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 3px' }} />
        <input type="date" value={fromSel} onChange={(e) => setFromSel(e.target.value)} style={dateInp} aria-label="Från datum" />
        <span style={{ color: 'var(--text-secondary)', fontSize: 11.5 }}>–</span>
        <input type="date" value={toSel} onChange={(e) => setToSel(e.target.value)} style={dateInp} aria-label="Till datum" />
        {customActive && (
          <button onClick={() => { setFromSel(''); setToSel(''); }} style={chip(false)}>Rensa</button>
        )}
      </div>
      <TrendCompare data={data} formatVal={fmtVal} curColor={color} prevColor="#C2A878" />
      <div className="note">
        Fjolår = samma veckodag ifjol (−364 dgr); månad = samma kalendermånad. Personal% = lön ÷ omsättning (perioder med försumbar omsättning utesluts).{matt === 'omsh' ? ' OMS/h = omsättning ÷ arbetade timmar (Personalkollen).' : ''} Egen datumperiod väljs med datumfälten{customActive && customSpan > 92 ? ' (dag-upplösning kräver period ≤ 92 dagar)' : ''}.
      </div>
    </div>
  );
}

// ── DAG- & VECKOVY MED DATUMSÖKNING ────────────────────────────────
// YoY-utforskaren ovan ritar kurvan; den här ger siffrorna bakom den, per dag
// eller vecka, för valfritt datumspann.
//
// Presets ankras i SENASTE DAGEN MED FÖRSÄLJNING, inte i dag. För en
// säsongsenhet är "senaste 30 dagarna" räknat från en augustidag bara trettio
// tomma rader — då får man leta sig till vintern för hand varje gång.
export function DagVeckoVy({ enheter, color = '#1B2A4A' }) {
  const [units, setUnits]       = useState(null);
  const [gran, setGran]         = useState('dag');   // 'dag' | 'vecka'
  const [dagar, setDagar]       = useState(30);      // preset-längd
  const [fromSel, setFromSel]   = useState('');
  const [toSel, setToSel]       = useState('');
  const [visaAlla, setVisaAlla] = useState(false);

  useEffect(() => { getDailyUnits().then(setUnits).catch(() => setUnits([])); }, []);

  const dag = useMemo(() => {
    const enhSet = new Set(enheter);
    const oms = {}, lon = {};
    for (const u of (units || [])) {
      if (!enhSet.has(u.enhet)) continue;
      oms[u.datum] = (oms[u.datum] || 0) + (u.intakt || 0);
      lon[u.datum] = (lon[u.datum] || 0) + Math.abs(u.lon || 0);
    }
    return { oms, lon };
  }, [units, enheter]);

  const ankare = useMemo(() => {
    let s = null;
    for (const d in dag.oms) if (dag.oms[d] > 0 && (s === null || d > s)) s = d;
    return s;
  }, [dag]);

  const eget = !!(fromSel && toSel && fromSel <= toSel);

  const rader = useMemo(() => {
    if (!ankare && !eget) return [];
    const slut  = eget ? toSel   : ankare;
    const start = eget ? fromSel : shiftISO(slut, -(dagar - 1));
    // Taket skyddar mot att någon skriver 1900 i datumfältet.
    const sum = (map, f, t) => { let s = 0, n = 0; for (let d = f; d <= t && n < 800; d = shiftISO(d, 1), n++) s += map[d] || 0; return s; };

    const hinkar = [];
    if (gran === 'dag') {
      let n = 0;
      for (let d = start; d <= slut && n < 800; d = shiftISO(d, 1), n++) hinkar.push({ label: fmtDate(d), from: d, to: d });
    } else {
      const dow = (new Date(start + 'T12:00:00Z').getUTCDay() + 6) % 7;
      let mon = shiftISO(start, -dow), n = 0;
      while (mon <= slut && n < 800) {
        const sun = shiftISO(mon, 6);
        hinkar.push({
          label: `v${getISOWeek(new Date(mon + 'T12:00:00Z'))}`,
          sub: `${Number(mon.slice(8, 10))}/${Number(mon.slice(5, 7))}–${Number(sun.slice(8, 10))}/${Number(sun.slice(5, 7))}`,
          from: mon, to: sun,
        });
        mon = shiftISO(mon, 7); n++;
      }
    }

    return hinkar.map((b) => {
      const oms  = sum(dag.oms, b.from, b.to);
      const lon  = sum(dag.lon, b.from, b.to);
      const fjol = sum(dag.oms, shiftISO(b.from, -364), shiftISO(b.to, -364));
      return {
        ...b, oms, lon, fjol,
        diff: fjol > 0 ? Math.round((oms - fjol) / fjol * 100) : null,
        // Personal% på en dag med några hundralappar säger ingenting.
        pers: oms >= 5000 && lon > 0 ? Math.round(lon / oms * 100) : null,
      };
    }).reverse(); // senaste först
  }, [dag, ankare, eget, gran, dagar, fromSel, toSel]);

  const tot = rader.reduce((a, r) => ({ oms: a.oms + r.oms, fjol: a.fjol + r.fjol, lon: a.lon + r.lon }), { oms: 0, fjol: 0, lon: 0 });
  const totDiff = tot.fjol > 0 ? Math.round((tot.oms - tot.fjol) / tot.fjol * 100) : null;
  const visade  = visaAlla ? rader : rader.slice(0, 14);
  const knapp = (aktiv) => ({
    background: aktiv ? color : 'transparent', color: aktiv ? '#fff' : 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 20, padding: '3px 10px', fontSize: 11,
    cursor: 'pointer', fontFamily: 'system-ui,sans-serif', transition: 'all 0.15s',
  });
  const datumFalt = {
    border: '1px solid var(--border)', borderRadius: 8, padding: '3px 8px', fontSize: 11.5,
    fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)',
  };
  const krHel = (v) => Math.round(v).toLocaleString('sv-SE') + ' kr';
  const farg  = (v) => (v === null ? 'var(--text)' : v >= 0 ? '#3B6D11' : '#A32D2D');
  const dPct  = (v) => (v === null ? '–' : `${v >= 0 ? '+' : ''}${v}%`);

  if (units === null) {
    return <div className="card"><p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar dagsdata…</p></div>;
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>{gran === 'dag' ? 'Dag för dag' : 'Vecka för vecka'}</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <button onClick={() => setGran('dag')}   style={knapp(gran === 'dag')}>Dag</button>
          <button onClick={() => setGran('vecka')} style={knapp(gran === 'vecka')}>Vecka</button>
          <span style={{ width: 8 }} />
          {[14, 30, 90].map((d) => (
            <button key={d} onClick={() => { setDagar(d); setFromSel(''); setToSel(''); }} style={knapp(!eget && dagar === d)}>{d} dgr</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
        <span>Från</span>
        <input type="date" value={fromSel} onChange={(e) => setFromSel(e.target.value)} aria-label="Från datum" style={datumFalt} />
        <span>till</span>
        <input type="date" value={toSel} onChange={(e) => setToSel(e.target.value)} aria-label="Till datum" style={datumFalt} />
        {(fromSel || toSel) && <button onClick={() => { setFromSel(''); setToSel(''); }} style={knapp(false)}>Rensa</button>}
      </div>

      <div className="period-table">
        <div className="period-header period-row-6">
          <span>{gran === 'dag' ? 'Dag' : 'Vecka'}</span>
          <span>Intäkt</span>
          <span>Ifjol</span>
          <span>Δ%</span>
          <span>Lön</span>
          <span>Pers%</span>
        </div>
        <div className="period-row period-row-6" style={{ fontWeight: 700 }}>
          <span style={{ color: 'var(--text)' }}>Summa ({rader.length})</span>
          <span>{tot.oms  > 0 ? krHel(tot.oms)  : '–'}</span>
          <span>{tot.fjol > 0 ? krHel(tot.fjol) : '–'}</span>
          <span style={{ color: farg(totDiff) }}>{dPct(totDiff)}</span>
          <span>{tot.lon > 0 ? krHel(tot.lon) : '–'}</span>
          <span>{tot.oms > 0 && tot.lon > 0 ? `${Math.round(tot.lon / tot.oms * 100)}%` : '–'}</span>
        </div>
        {visade.map((r, i) => (
          <div key={i} className="period-row period-row-6">
            <span>
              {r.label}
              {r.sub && <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>{r.sub}</div>}
            </span>
            <span>{r.oms  > 0 ? krHel(r.oms)  : '–'}</span>
            <span>{r.fjol > 0 ? krHel(r.fjol) : '–'}</span>
            <span style={{ color: farg(r.diff) }}>{dPct(r.diff)}</span>
            <span>{r.lon > 0 ? krHel(r.lon) : '–'}</span>
            <span>{r.pers !== null ? `${r.pers}%` : '–'}</span>
          </div>
        ))}
        {rader.length === 0 && (
          <div className="period-row period-row-6"><span>Ingen data för perioden</span><span>–</span><span>–</span><span>–</span><span>–</span><span>–</span></div>
        )}
      </div>
      <VisaFlerRad total={rader.length} expanded={visaAlla} onToggle={() => setVisaAlla((v) => !v)} initial={14} />
      <div className="note" style={{ marginTop: 10 }}>
        Källa: Qlik — Datum × Resultatenhet. Ifjol = samma veckodagar (−364 dgr){eget ? '' : `; perioden räknas bakåt från ${ankare ? fmtDate(ankare) : 'senaste dagen'} — senaste dagen med försäljning`}. Personal% döljs för perioder under 5 tkr.
      </div>
    </div>
  );
}
